import { useEffect, useState } from "react";
import { Form, Col, Row } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircleOutlined,
  CloseOutlined,
  UnorderedListOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import useGet from "../../../global/hooks/useGet";
import usePost from "./../../../global/hooks/usePost";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "./../../../global/components/CommonButton";
import LicenseOpIcon from "./../../../assets/images/vectors/SideBarIcons/LicenseOpIcon";
import ConfirmModal from "../../../global/components/ConfirmModal";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { getChangedFields } from "../../../utils/getChangedFields";
import usePut from "../../../global/hooks/usePut";

export const SipForm = ({ type }) => {
  const [nextNo, setNextNo] = useState();
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const url = id
    ? `${process.env.REACT_APP_API_URL}/api/sips/${id}/`
    : `${process.env.REACT_APP_API_URL}/api/sips/`;
  const sipIdSortedurl = `${process.env.REACT_APP_API_URL}/api/sips/?limit=10&sort_by=SipId&order=desc`;
  // const postUrl = `${process.env.REACT_APP_API_URL}/api/sips/`;
  // const putUrl = `${process.env.REACT_APP_API_URL}/api/sips/${id}/`;
  // const deleteUrl = `${process.env.REACT_APP_API_URL}/api/sips/${id}/`;

  const { data: sipIdSortedData, loading: sipLoading } = useGet(
    type === "info" || type === "edit" ? null : sipIdSortedurl
  );

  const { data, loading, error, refetch } = useGet(url);

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(url);
  const {
    data: putData,
    loading: putLoading,
    error: putError,
    putData: putAction,
  } = usePut(url);
  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteData,
  } = useDelete(url);

  const onFinish = async (values) => {
    if (!id && sipIdSortedData?.data && Array.isArray(sipIdSortedData?.data)) {
      if (
        values.SipId &&
        Number(values.SipId) < Number(sipIdSortedData?.data[0]?.SipId) + 1
      ) {
        return form.setFields([
          {
            name: "SipId",
            errors: [
              `SIP ID can start from ${
                Number(sipIdSortedData?.data[0]?.SipId) + 1
              }`,
            ], // Set validation error manually
          },
        ]);
      }
    }
    try {
      if (id) {
        const changedFields = getChangedFields(data?.data, values);
        await putAction(changedFields);
      } else {
        await postAction(values);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  const onReset = () => {
    // form.resetFields();
    navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  //toat for create
  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong"
      );
    } else if (!postLoading && !postError && postData) {
      openConfirmNotification("Success");
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [postError, postData, postLoading]);

  //toat for update
  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong"
      );
    } else if (!putLoading && !putError && putData) {
      openConfirmNotification("Success");
      refetch();
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [putData, putLoading, putError]);

  useEffect(() => {
    if (
      Array.isArray(sipIdSortedData?.data) &&
      sipIdSortedData.data.length > 0
    ) {
      const nextOp = Number(sipIdSortedData.data[0].SipId);

      setNextNo(nextOp + 1);
    }
  }, [sipLoading, sipIdSortedData]);

  // Set default field value
  useEffect(() => {
    if (!id) {
      form.setFieldsValue({
        SipId: String(nextNo),
      });
    }
  }, [nextNo]);

  // Set default field value
  useEffect(() => {
    if (!id || loading) return;
    const getSipInfoById = () => {
      try {
        form.resetFields();
        if (data?.data) {
          form.setFieldsValue({
            OptionNo: data?.data?.SipNo ? String(data?.data?.SipNo) : "",
            SipId: data.data.SipId ? String(data?.data?.SipId) : "",
            Password: data.data.Password ? String(data?.data?.Password) : "",
            Mac: data.data.Mac ? String(data?.data?.Mac) : "",
          });
        } else {
          console.warn("No data found for the given ID");
        }
      } catch (error) {
        if (!loading) {
          console.log({ error });
          openErrorNotification("Error fetching data!");
        }
      }
    };

    getSipInfoById();
  }, [id, data, loading, form, type]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      // refetch();
      navigate("/sip");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong"
      );
    } else {
      return;
    }
  }, [deletedData, deletedDataError]);

  const breadcrumbItems = [
    {
      label: "SIP",
      href: "/sip",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/sip/info/${id}`
          : `/sip/edit/${id}`
        : "/sip/add",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              navigate(`/sip/edit/${id}`);
            },
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => {
              setShowModal(true);
            },
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => {
              navigate(-1);
            },
          },
        ]
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>

          <div className="border bg-white rounded-[4px] overflow-hidden">
            <div className="flex items-center bg-gray-200 p-2.5">
              <LicenseOpIcon className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                SIP
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 16]}>
                {/* <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <CustomInput
                        label="No"
                        name="SipNo"
                        disabled={type === "info"}
                        type="number"
                        required
                      />
                    </Col> */}
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="SIP ID"
                    name="SipId"
                    disabled={type === "info"}
                    required
                    type="number"
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Password"
                    name="Password"
                    disabled={type === "info"}
                    autoComplete="new-password"
                    required
                    loading={loading}
                  />
                </Col>
              </Row>
              {type === "info" && (
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="MAC"
                      name="Mac"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                </Row>
              )}
            </div>
          </div>
        </div>
      </Form>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </>
  );
};
